import api from '@/services/api';
import type {
    ApiItemResponse,
    ApiListResponse,
    CreateDealerPayload,
    Dealer,
    DealerSearchParams,
    DealerSearchResponse,
    DealerStatus,
    DealerSummary,
    DealersByIdsResponse,
    UpdateDealerPayload,
} from '../types/dealers.types';
import { serializeBracketedArrayParams } from './bracketedArrayParams';

const dealersPath = '/dealers';

const toDealerUrl = (id: string): string => `${dealersPath}/${id}`;

export const fetchDealers = async (params: DealerSearchParams = {}): Promise<DealerSearchResponse> => {
    const response = await api.get<DealerSearchResponse>(`${dealersPath}/`, {
        params,
        paramsSerializer: { serialize: serializeBracketedArrayParams },
    });

    if (!response.data.success) {
        throw new Error('Failed to fetch dealers');
    }

    return response.data;
};

export const fetchDealerById = async (
    id: string,
    opts?: { suppressForbiddenToast?: boolean },
): Promise<Dealer> => {
    const response = await api.get<ApiItemResponse<Dealer>>(toDealerUrl(id), {
        suppressGlobalForbiddenToast: opts?.suppressForbiddenToast,
    });

    if (!response.data.success) {
        throw new Error('Failed to fetch dealer');
    }

    return response.data.data;
};

export const createDealer = async (payload: CreateDealerPayload): Promise<Dealer> => {
    const response = await api.post<ApiItemResponse<Dealer>>(`${dealersPath}/`, payload);

    if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create dealer');
    }

    return response.data.data;
};

export const updateDealer = async (id: string, payload: UpdateDealerPayload): Promise<Dealer> => {
    const response = await api.put<ApiItemResponse<Dealer>>(toDealerUrl(id), payload);

    if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update dealer');
    }

    return response.data.data;
};

export const deleteDealer = async (id: string): Promise<void> => {
    const response = await api.delete<ApiItemResponse<null>>(toDealerUrl(id));

    if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete dealer');
    }
};

export const deactivateDealer = async (id: string): Promise<Dealer> => {
    const response = await api.post<ApiItemResponse<Dealer>>(`${toDealerUrl(id)}/deactivate`);

    if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to deactivate dealer');
    }

    return response.data.data;
};

export const reactivateDealer = async (id: string): Promise<Dealer> => {
    const response = await api.post<ApiItemResponse<Dealer>>(`${toDealerUrl(id)}/reactivate`);

    if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reactivate dealer');
    }

    return response.data.data;
};

export const fetchDealerGroupMembers = async (dealerId: string): Promise<Dealer[]> => {
    const response = await api.get<ApiListResponse<Dealer>>(`${toDealerUrl(dealerId)}/group/members`);

    if (!response.data.success) {
        throw new Error('Failed to fetch dealer group members');
    }

    return response.data.data;
};

/**
 * Batched dealer lookup by IDs. Returns a (possibly shorter) array — missing
 * IDs are silently omitted by the server and must be handled by the caller.
 *
 * Uses an explicit paramsSerializer because the default axios v1 serialiser
 * emits `ids=a&ids=b` (no brackets), which PHP's `validate(['ids' => 'array'])`
 * does NOT parse as an array. PHP needs `ids[]=a&ids[]=b`.
 */
export const fetchDealerStatuses = async (): Promise<DealerStatus[]> => {
    const response = await api.get<{ success: boolean; data: DealerStatus[] }>(`${dealersPath}/statuses`);

    if (!response.data.success) {
        throw new Error('Failed to fetch dealer statuses');
    }

    return response.data.data;
};

export const fetchDealersByIds = async (ids: string[]): Promise<DealerSummary[]> => {
    const unique = Array.from(new Set(ids.filter(Boolean))).sort();
    if (unique.length === 0) {
        return [];
    }

    const response = await api.get<DealersByIdsResponse>(`${dealersPath}/by-ids`, {
        params: { ids: unique },
        paramsSerializer: { serialize: serializeBracketedArrayParams },
    });

    if (!response.data.success) {
        throw new Error('Failed to fetch dealers by IDs');
    }

    return response.data.data;
};
